/**
 * Secure in-memory authentication store
 * Replaces localStorage/sessionStorage for sensitive auth tokens
 */

interface AuthData {
  token?: string;
  refreshToken?: string;
  user?: any;
  expiresAt?: number;
  lastActivity?: number;
}

class InMemoryAuthStore {
  private authData: AuthData = {};
  private broadcastChannel: BroadcastChannel | null = null;
  private idleTimer: NodeJS.Timeout | null = null;
  private sessionTimer: NodeJS.Timeout | null = null;
  private readonly storageKey = 'rugira_demo_auth';

  // Configuration
  private readonly IDLE_TIMEOUT = 15 * 60 * 1000; // 15 minutes
  private readonly SESSION_TTL = 12 * 60 * 60 * 1000; // 12 hours
  private sessionTtlMs = this.SESSION_TTL;
  private readonly ACTIVITY_CHECK_INTERVAL = 60 * 1000; // Check every minute

  constructor() {
    this.hydrateFromStorage();
    this.ensureDemoDefaults();
    this.initBroadcastChannel();
    this.initActivityListeners();
    this.initSessionManagement();
  }

  private ensureDemoDefaults() {
    const env = (typeof import.meta !== 'undefined' ? import.meta.env : undefined) as any;
    if (!env || env.VITE_DEMO_MODE !== '1') {
      return;
    }
    if ((env.VITE_AUTH_MODE ?? '').toString().trim().toLowerCase() === 'demo') {
      return;
    }

    const devToken: string | undefined = env.VITE_DEV_TOKEN;
    const devTenant: string = env.VITE_DEV_TENANT_ID || 'default';

    let updated = false;
    if (devToken && this.authData.token !== devToken) {
      this.authData.token = devToken;
      this.authData.expiresAt = Date.now() + this.SESSION_TTL;
      updated = true;
    }

    if (!this.authData.user) {
      this.authData.user = {
        email: env.VITE_DEV_EMAIL || 'admin@rugira.ch',
        current_tenant: devTenant,
        tenant_id: devTenant,
      };
      updated = true;
    }

    if (updated) {
      this.authData.lastActivity = Date.now();
      this.persistAuthData();
      this.resetIdleTimer();
      this.resetSessionTimer();
    }
  }

  private shouldPersist(): boolean {
    if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
      return false;
    }

    if (typeof import.meta !== 'undefined' && import.meta.env) {
      const disableFlag = (import.meta.env.VITE_DISABLE_AUTH_PERSIST ?? '').toString().trim().toLowerCase();
      if (['1', 'true', 'yes', 'on'].includes(disableFlag)) {
        return false;
      }
    }

    return true;
  }

  private hydrateFromStorage() {
    if (!this.shouldPersist()) {
      return;
    }

    try {
      const stored = sessionStorage.getItem(this.storageKey);
      if (!stored) {
        return;
      }

      const parsed = JSON.parse(stored) as AuthData;
      if (!parsed || (parsed.expiresAt && parsed.expiresAt <= Date.now())) {
        sessionStorage.removeItem(this.storageKey);
        return;
      }

      this.authData = {
        ...parsed,
        lastActivity: parsed.lastActivity ?? Date.now()
      };

      if (this.authData.expiresAt) {
        this.sessionTtlMs = this.authData.expiresAt - Date.now();
      }

      this.resetIdleTimer();
      this.resetSessionTimer();
    } catch (error) {
      console.warn('Failed to restore persisted auth state', error);
      sessionStorage.removeItem(this.storageKey);
    }
  }

  private persistAuthData() {
    if (!this.shouldPersist()) {
      return;
    }

    try {
      const snapshot: AuthData = {
        token: this.authData.token,
        user: this.authData.user,
        expiresAt: this.authData.expiresAt,
        lastActivity: this.authData.lastActivity
      };
      sessionStorage.setItem(this.storageKey, JSON.stringify(snapshot));
    } catch (error) {
      console.warn('Failed to persist demo auth state', error);
    }
  }

  private clearPersistedAuth() {
    if (!this.shouldPersist()) {
      return;
    }
    sessionStorage.removeItem(this.storageKey);
  }

  private isLogoutInProgress(): boolean {
    try {
      const timestamp = localStorage.getItem('rugira_logout_guard');
      if (timestamp) {
        const guardAge = Date.now() - Number.parseInt(timestamp, 10);
        if (guardAge < 30000) {
          return true;
        }
      }
      if (sessionStorage.getItem('rugira_logout_guard') === '1') {
        return true;
      }
    } catch {
      // ignore
    }
    return false;
  }

  private initBroadcastChannel() {
    try {
      this.broadcastChannel = new BroadcastChannel('auth_channel');

      this.broadcastChannel.onmessage = (event) => {
        if (event.data.type === 'logout') {
          // Skip if logout is already in progress in this tab
          if (this.isLogoutInProgress()) {
            if (process.env.NODE_ENV === 'development' || import.meta.env?.DEV) {
              console.log('[authStore] BroadcastChannel logout ignored - already in progress');
            }
            return;
          }
          this.clear();
          window.location.href = '/';
        }
        if (event.data.type === 'auth_update') {
          this.authData = event.data.authData;
          if (typeof this.authData.expiresAt === 'number') {
            this.sessionTtlMs = this.authData.expiresAt - Date.now();
          }
          this.persistAuthData();
          this.resetIdleTimer();
          this.resetSessionTimer();
        }
      };
    } catch (e) {
      console.warn('BroadcastChannel not supported');
    }
  }

  private initActivityListeners() {
    // Track user activity
    const updateActivity = () => {
      this.authData.lastActivity = Date.now();
      this.resetIdleTimer();
      this.persistAuthData();
    };

    // Listen for user activity
    ['mousedown', 'keydown', 'scroll', 'touchstart'].forEach(event => {
      document.addEventListener(event, updateActivity, { passive: true });
    });

    // Handle visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        // Optional: Could clear sensitive data when tab is hidden
        // For now, just stop the idle timer
        if (this.idleTimer) {
          clearTimeout(this.idleTimer);
        }
      } else {
        // Resume idle timer when tab becomes visible
        this.resetIdleTimer();
        this.checkSessionValidity();
      }
    });

    // Clear on tab close
    window.addEventListener('beforeunload', () => {
      // Note: In production, tokens should be cleared
      // For development, we might want to preserve them
      if (process.env.NODE_ENV === 'production') {
        this.clear();
      }
    });
  }

  private initSessionManagement() {
    // Check session validity every minute
    setInterval(() => {
      this.checkSessionValidity();
    }, this.ACTIVITY_CHECK_INTERVAL);
  }

  private resetIdleTimer() {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
    }

    this.idleTimer = setTimeout(() => {
      this.lockScreen();
    }, this.IDLE_TIMEOUT);
  }

  private resetSessionTimer() {
    if (this.sessionTimer) {
      clearTimeout(this.sessionTimer);
    }

    this.sessionTimer = setTimeout(() => {
      this.forceReauth();
    }, this.sessionTtlMs);
  }

  private checkSessionValidity() {
    const now = Date.now();

    // Check if session has expired
    if (this.authData.expiresAt && now > this.authData.expiresAt) {
      this.forceReauth();
      return;
    }

    // Check idle timeout
    if (this.authData.lastActivity) {
      const idleTime = now - this.authData.lastActivity;
      if (idleTime > this.IDLE_TIMEOUT) {
        this.lockScreen();
      }
    }
  }

  private lockScreen() {
    // Dispatch event for UI to show lock screen
    window.dispatchEvent(new CustomEvent('auth:locked', {
      detail: { reason: 'idle_timeout' }
    }));
  }

  private forceReauth() {
    // Check if logout is already in progress to prevent redirect loops
    const logoutGuard = localStorage.getItem('rugira_logout_guard');
    if (logoutGuard) {
      const guardAge = Date.now() - Number.parseInt(logoutGuard, 10);
      if (guardAge < 30000) {
        // Logout is in progress, don't force reauth
        return;
      }
    }
    if (sessionStorage.getItem('rugira_logout_guard') === '1') {
      return;
    }

    this.clear();
    // Dispatch event for UI to redirect to login
    window.dispatchEvent(new CustomEvent('auth:expired', {
      detail: { reason: 'session_expired' }
    }));
    this.clearPersistedAuth();
    window.location.href = '/';
  }

  // Public methods
  setAuth(token?: string, refreshToken?: string, user?: any, ttlMs?: number) {
    const now = Date.now();
    const effectiveTtl =
      typeof ttlMs === 'number' && Number.isFinite(ttlMs) && ttlMs > 0
        ? ttlMs
        : this.SESSION_TTL;
    this.sessionTtlMs = effectiveTtl;
    this.authData = {
      token,
      refreshToken,
      user,
      expiresAt: now + effectiveTtl,
      lastActivity: now
    };

    this.persistAuthData();

    // Reset timers
    this.resetIdleTimer();
    this.resetSessionTimer();

    // Broadcast to other tabs
    this.broadcastChannel?.postMessage({
      type: 'auth_update',
      authData: this.authData
    });
  }

  setSessionUser(user: any, ttlMs?: number) {
    this.setAuth(undefined, undefined, user, ttlMs);
  }

  getToken(): string | undefined {
    this.checkSessionValidity();
    return this.authData.token;
  }

  getRefreshToken(): string | undefined {
    return this.authData.refreshToken;
  }

  getUser(): any {
    return this.authData.user;
  }

  isAuthenticated(): boolean {
    return !!this.authData.token && !this.isExpired();
  }

  isExpired(): boolean {
    if (!this.authData.expiresAt) return true;
    return Date.now() > this.authData.expiresAt;
  }

  clear() {
    this.authData = {};

    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }

    if (this.sessionTimer) {
      clearTimeout(this.sessionTimer);
      this.sessionTimer = null;
    }

    this.clearPersistedAuth();
  }

  logout() {
    this.clear();

    // Broadcast logout to other tabs
    this.broadcastChannel?.postMessage({
      type: 'logout'
    });
  }

  // Utility to clear all browser storage (for settings)
  clearAllStorage() {
    let localStorageLogoutGuard: string | null = null;

    // Preserve logout guard from localStorage before any cleanup
    // This is critical for proper logout flow
    try {
      localStorageLogoutGuard = localStorage.getItem('rugira_logout_guard');
    } catch (e) {
      console.warn('Could not read localStorage logout guard:', e);
    }

    // Clear localStorage
    try {
      localStorage.clear();
      if (localStorageLogoutGuard) {
        localStorage.setItem('rugira_logout_guard', localStorageLogoutGuard);
      }
    } catch (e) {
      console.warn('Could not clear localStorage:', e);
    }

    // Clear sessionStorage (except logout guard which we need to persist)
    try {
      const logoutGuard = sessionStorage.getItem('rugira_logout_guard');
      sessionStorage.clear();
      if (logoutGuard) {
        sessionStorage.setItem('rugira_logout_guard', logoutGuard);
      }
    } catch (e) {
      console.warn('Could not clear sessionStorage:', e);
    }

    // Clear IndexedDB
    if ('indexedDB' in window) {
      indexedDB.databases().then(databases => {
        databases.forEach(db => {
          if (db.name) {
            indexedDB.deleteDatabase(db.name);
          }
        });
      }).catch(e => {
        console.warn('Could not clear IndexedDB:', e);
      });
    }

    // Clear all cookies including session cookies
    // Try multiple cookie names and paths to ensure complete cleanup
    const cookieNamesToExpire = [
      'qf_sid',           // Standard session cookie
      '__Host-qf_sid',    // Secure prefix variant
      'session_id',       // Alternative session cookie name
      'rugira_session',   // Potential session cookie
    ];

    // Clear cookies with various path and domain combinations
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const expiration = new Date(0).toUTCString();

    cookieNamesToExpire.forEach(name => {
      // Clear with path=/
      document.cookie = `${name}=;expires=${expiration};path=/`;
      // Clear with path=/ and domain
      if (hostname) {
        document.cookie = `${name}=;expires=${expiration};path=/;domain=${hostname}`;
        // Also try parent domain for subdomains
        const parts = hostname.split('.');
        if (parts.length > 2) {
          const parentDomain = parts.slice(-2).join('.');
          document.cookie = `${name}=;expires=${expiration};path=/;domain=.${parentDomain}`;
        }
      }
    });

    // Clear all other client-accessible cookies
    document.cookie.split(";").forEach(c => {
      const cookiePart = c.trim();
      if (cookiePart) {
        const eqPos = cookiePart.indexOf("=");
        const name = eqPos > -1 ? cookiePart.substring(0, eqPos) : cookiePart;
        document.cookie = `${name}=;expires=${expiration};path=/`;
      }
    });

    if (process.env.NODE_ENV === 'development' || (typeof import.meta !== 'undefined' && import.meta.env?.DEV)) {
      console.log('[authStore] Cleared all storage and cookies');
    }
  }
}

// Export singleton instance
export const authStore = new InMemoryAuthStore();
